import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useChatContext } from '../context/ChatContext';
import { useAnnouncer } from '../context/AccessibilityContext';
import { useChatActions } from '../hooks/useChatActions';
import { KEYBOARD_KEYS } from '../utils/accessibility';
import { VoiceButton } from './VoiceButton';
import './MessageInput.css';

interface MessageInputProps {
  onSendMessage: (content: string) => void;
  onVoiceData?: (audioBlob: Blob) => void;
  onVoiceStart?: () => void;
  onVoiceStop?: () => void;
  onTypingChange: (isTyping: boolean) => void;
  disabled?: boolean;
  placeholder?: string;
}

export const MessageInput: React.FC<MessageInputProps> = ({
  onSendMessage,
  onVoiceData,
  onVoiceStart,
  onVoiceStop,
  onTypingChange,
  disabled = false,
  placeholder
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { state } = useChatContext();
  const { showIntro } = state;
  const { dismissIntro } = useChatActions();
  const announcer = useAnnouncer();
  const { t } = useTranslation();
  
  const defaultPlaceholder = placeholder || t('messageInput.placeholder');

  // Handle typing indicator logic
  const handleTypingChange = useCallback((typing: boolean) => {
    if (typing !== isTyping) {
      setIsTyping(typing);
      onTypingChange(typing);
    }
  }, [isTyping, onTypingChange]);

  // Clear typing timeout
  const clearTypingTimeout = useCallback(() => {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
  }, []);

  // Handle input change
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setInputValue(value);

    // Dismiss intro on first interaction
    if (showIntro && value.length > 0) {
      dismissIntro();
    }

    // Handle typing indicators
    if (value.length > 0 && !isTyping) {
      handleTypingChange(true);
    }

    // Clear existing timeout
    clearTypingTimeout();

    // Set new timeout to stop typing indicator after 3 seconds of inactivity
    if (value.length > 0) {
      typingTimeoutRef.current = setTimeout(() => {
        handleTypingChange(false);
      }, 3000);
    } else {
      handleTypingChange(false);
    }
  }, [showIntro, isTyping, handleTypingChange, clearTypingTimeout, dismissIntro]);

  // Handle message sending directly (no queuing needed with lazy connections)
  const sendMessage = useCallback(async (content: string): Promise<boolean> => {
    try {
      // Send message directly - connection will be established automatically
      onSendMessage(content);
      return true;
    } catch (error) {
      console.error('Failed to send message:', error);
      return false;
    }
  }, [onSendMessage]);

  // Handle form submission
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    const trimmedValue = inputValue.trim();
    if (!trimmedValue || disabled) return;

    // Clear typing indicator immediately when sending
    clearTypingTimeout();
    handleTypingChange(false);

    // Send the message directly
    await sendMessage(trimmedValue);

    // Clear input and maintain focus
    setInputValue('');
    if (inputRef.current) {
      inputRef.current.focus();
    }

    // Dismiss intro on message send
    if (showIntro) {
      dismissIntro();
    }
  }, [inputValue, disabled, sendMessage, handleTypingChange, clearTypingTimeout, showIntro, dismissIntro]);

  // Auto-resize textarea
  const adjustTextareaHeight = useCallback(() => {
    if (inputRef.current) {
      // Reset height to auto to get the correct scrollHeight
      inputRef.current.style.height = 'auto';
      // Set height based on content, with min 20px and max 120px
      const newHeight = Math.max(20, Math.min(inputRef.current.scrollHeight, 120));
      inputRef.current.style.height = `${newHeight}px`;
    }
  }, []);

  // Adjust height when input value changes
  useEffect(() => {
    adjustTextareaHeight();
  }, [inputValue, adjustTextareaHeight]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      clearTypingTimeout();
    };
  }, [clearTypingTimeout]);

  // Handle voice data
  const handleVoiceData = useCallback((audioBlob: Blob) => {
    if (onVoiceData) {
      onVoiceData(audioBlob);
      
      // Dismiss intro on voice interaction
      if (showIntro) {
        dismissIntro();
      }
    }
  }, [onVoiceData, showIntro, dismissIntro]);

  // Focus input on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Handle Enter key to send message
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
      return;
    }
    
    // Handle Escape key to clear input
    if (e.key === KEYBOARD_KEYS.ESCAPE) {
      setInputValue('');
      clearTypingTimeout();
      handleTypingChange(false);
      announcer.announce(t('messageInput.inputCleared'), 'polite');
    }
  }, [clearTypingTimeout, handleTypingChange, announcer, handleSubmit, t]);

  const inputId = 'message-input';
  const helpId = 'message-input-help';

  return (
    <section 
      className="message-input" 
      role="region" 
      aria-label={t('messageInput.messageComposition')}
    >
      <form onSubmit={handleSubmit} noValidate>
        <div className="message-input__container">
          <label htmlFor={inputId} className="sr-only">
            {t('messageInput.typeYourMessage')}
          </label>
          <textarea
            ref={inputRef}
            id={inputId}
            className="message-input__textarea accessible-input"
            value={inputValue}
            onChange={handleInputChange}
            onInput={adjustTextareaHeight}
            onKeyDown={handleKeyDown}
            placeholder={defaultPlaceholder}
            disabled={disabled}
            rows={1}
            aria-label={t('messageInput.typeYourMessage')}
            aria-describedby={helpId}
            aria-required="false"
            autoComplete="off"
            spellCheck="true"
          />
          <div className="message-input__actions" role="group" aria-label={t('messageInput.messageActions')}>
            {inputValue.trim() ? (
              <button
                type="submit"
                className="message-input__send-button accessible-button"
                disabled={disabled}
                aria-label={t('messageInput.sendMessage', { 
                  message: inputValue.slice(0, 50) + (inputValue.length > 50 ? '...' : '') 
                })}
              >
                <svg
                  className="message-input__send-icon"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                </svg>
                <span className="sr-only">{t('common.send')}</span>
              </button>
            ) : (
              <div className="message-input__mic-button-wrapper">
                <VoiceButton
                  onVoiceData={handleVoiceData}
                  onVoiceStart={onVoiceStart}
                  onVoiceStop={onVoiceStop}
                  isRecording={state.isRecording}
                  disabled={disabled}
                />
              </div>
            )}
          </div>
        </div>
        
        {/* Help text - clean without connection status */}
        <div id={helpId} className="message-input__help" role="status">
          <span>{t('messageInput.helpText')}</span>
        </div>

        {/* Send button help for empty input */}
        <div id="send-button-help" className="sr-only">
          {t('messageInput.sendButtonHelp')}
        </div>

        {/* Typing indicator announcement for screen readers */}
        <div className="sr-only" aria-live="polite" aria-atomic="true">
          {isTyping ? t('messageInput.youAreTyping') : ''}
        </div>
      </form>
    </section>
  );
};